/**
 * Application chrome: navigation, identity, sign-out.
 *
 * The navigation is filtered by the shared permission matrix, so a role never
 * sees a link to a page the API would refuse. That is a usability decision
 * rather than a security one — `RequirePermissionRoute` still guards each page,
 * and the endpoints behind them are gated server-side — but it is the reason
 * the Legal Production sidebar is short rather than full of dead ends.
 */
import { useEffect, useState, type ComponentType, type ReactNode, type SVGProps } from 'react'
import { NavLink, useLocation, useNavigate } from 'react-router-dom'
import type { Permission } from '@accounting/shared'
import { apiFetch } from '../lib/api'
import { useSession } from '../lib/session'
import { cx } from '../ui/cx'
import { RoleBadge } from '../ui/Badge'
import { Wordmark } from '../ui/Wordmark'
import { NotificationBell } from './NotificationBell'
import {
  AccountIcon,
  AlertBellIcon,
  BudgetIcon,
  CashIcon,
  CloseIcon,
  ExecutiveIcon,
  FlagIcon,
  ForecastIcon,
  LedgerIcon,
  MenuIcon,
  OverviewIcon,
  ProductionIcon,
  ReceivableIcon,
  RefundIcon,
  ShieldIcon,
  SignOutIcon,
  SyncIcon,
  TrustIcon,
  UsersIcon,
  WeekIcon,
} from '../ui/icons'

interface NavItem {
  to: string
  label: string
  icon: ComponentType<SVGProps<SVGSVGElement>>
  /** Undefined means every signed-in account may see it. */
  permission?: readonly Permission[]
}

interface NavGroup {
  heading: string | null
  items: NavItem[]
}

const NAVIGATION: NavGroup[] = [
  {
    heading: null,
    items: [
      { to: '/', label: 'Overview', icon: OverviewIcon },
      {
        to: '/executive',
        label: 'Executive dashboard',
        icon: ExecutiveIcon,
        /**
         * Ungrouped, beside Overview, rather than filed under Billing or
         * Receivables. It belongs to none of them — it reads all of them — and
         * burying the consolidated view inside one module's group would suggest
         * it was that module's screen.
         */
        permission: ['firm_financials.view'],
      },
      {
        to: '/weekly',
        label: 'Weekly dashboard',
        icon: WeekIcon,
        permission: ['firm_financials.view'],
      },
      {
        to: '/alerts',
        label: 'Exception alerts',
        icon: AlertBellIcon,
        /**
         * Ungrouped beside Overview and the executive dashboard, for the same
         * reason that one is: alerts read every module and belong to none of
         * them. Filing them under Billing would suggest they were billing's.
         */
        permission: ['alerts.view'],
      },
    ],
  },
  {
    heading: 'Billing',
    items: [
      {
        to: '/billing-exceptions',
        label: 'Exceptions',
        icon: FlagIcon,
        /** Either grant reaches the queue; they differ in which rows it shows. */
        permission: ['billing_exceptions.view_all', 'billing_exceptions.view_own_matters'],
      },
      {
        to: '/billing-forecast',
        label: 'Forecast',
        icon: ForecastIcon,
        permission: ['firm_financials.view'],
      },
    ],
  },
  {
    heading: 'Receivables',
    items: [
      {
        to: '/accounts-receivable',
        label: 'Accounts receivable',
        icon: ReceivableIcon,
        /** Its own grant, not the forecast's. See the permission matrix. */
        permission: ['ar.view'],
      },
      {
        to: '/refunds',
        label: 'Client refunds',
        icon: RefundIcon,
        /** Reading refunds; deciding on them is `refunds.manage`, checked in-page. */
        permission: ['refunds.view'],
      },
    ],
  },
  {
    heading: 'Production',
    items: [
      {
        to: '/productivity',
        label: 'Legal production',
        icon: ProductionIcon,
        /** Reading the pack; editing and sending are checked in-page. */
        permission: ['reports.view'],
      },
    ],
  },
  {
    heading: 'Cash',
    items: [
      {
        to: '/cash-flow',
        label: 'Cash flow',
        icon: CashIcon,
        /** Reading the forecast; maintaining obligations is checked in-page. */
        permission: ['cash_flow.view'],
      },
      {
        to: '/budget',
        label: 'Budget vs. actual',
        icon: BudgetIcon,
        /** Reading the comparison; importing and scenario-building are in-page. */
        permission: ['budgets.view'],
      },
    ],
  },
  {
    heading: 'Client money',
    items: [
      {
        to: '/trust-funding',
        label: 'Trust funding',
        icon: TrustIcon,
        /** IOLTA balances and the movement history. Finance only, per the matrix. */
        permission: ['trust_ledger.view'],
      },
    ],
  },
  {
    heading: 'Administration',
    items: [
      { to: '/users', label: 'Users', icon: UsersIcon, permission: ['users.manage'] },
      { to: '/roles', label: 'Roles & permissions', icon: ShieldIcon },
      { to: '/audit-log', label: 'Audit log', icon: LedgerIcon, permission: ['audit_log.view'] },
      {
        to: '/integrations',
        label: 'Integrations',
        icon: SyncIcon,
        permission: ['integrations.manage'],
      },
    ],
  },
]

export function AppShell({ children }: { children: ReactNode }) {
  const [menuOpen, setMenuOpen] = useState(false)
  const location = useLocation()

  /** A slide-over left open across a navigation would cover the page it opened. */
  useEffect(() => setMenuOpen(false), [location.pathname])

  return (
    <div className="flex min-h-full">
      {/**
        * Pinned to the viewport rather than stretched down the page: on a long
        * page the sign-out control at the foot of the sidebar would otherwise
        * sit a full scroll away from where the reader is.
        */}
      <div className="hidden md:sticky md:top-0 md:block md:h-screen md:w-64 md:shrink-0 md:self-start">
        <Sidebar />
      </div>

      <div className="flex min-w-0 flex-1 flex-col">
        {/**
          * One header on every viewport, where it used to be mobile-only.
          *
          * The notification centre has to be reachable from every page — it is
          * the durable record of every alert, and one you have to navigate to
          * is one nobody checks — so it needs somewhere persistent to live. On
          * mobile this stays the brand bar carrying the menu and the wordmark;
          * on desktop those are in the sidebar already, so it reduces to a thin
          * white rule with the bell on the right.
          */}
        <header className="flex items-center gap-3 border-b border-ink-200 bg-brand-900 px-4 py-3 md:bg-white md:px-8">
          <button
            type="button"
            onClick={() => setMenuOpen(true)}
            aria-expanded={menuOpen}
            className="rounded-md p-1.5 text-brand-100 hover:bg-white/10 md:hidden"
          >
            <MenuIcon />
            <span className="sr-only">Open navigation</span>
          </button>
          <div className="md:hidden">
            <Wordmark />
          </div>
          <div className="ml-auto">
            <NotificationBell />
          </div>
        </header>

        <main className="mx-auto w-full max-w-[96rem] flex-1 px-4 py-8 sm:px-8">{children}</main>
      </div>

      {menuOpen && (
        <div className="fixed inset-0 z-20 md:hidden">
          <button
            type="button"
            aria-label="Close navigation"
            onClick={() => setMenuOpen(false)}
            className="absolute inset-0 bg-brand-950/50"
          />
          <div className="relative h-full w-72 max-w-[85%]">
            <Sidebar onDismiss={() => setMenuOpen(false)} />
          </div>
        </div>
      )}
    </div>
  )
}

function Sidebar({ onDismiss }: { onDismiss?: () => void }) {
  const { user, may, signOut } = useSession()
  const navigate = useNavigate()

  async function handleSignOut() {
    /**
     * Revoke the token server-side before dropping it locally. Clearing it in
     * the browser alone would leave a working credential valid for up to twelve
     * hours — and if the sign-out request fails, the local session still ends,
     * because a user who pressed sign out must end up signed out.
     */
    try {
      await apiFetch('/auth/logout', { method: 'POST' })
    } catch {
      // Intentionally ignored; see above.
    }
    signOut()
    navigate('/login', { replace: true })
  }

  return (
    <div className="flex h-full flex-col bg-brand-900 text-brand-100">
      <div className="flex items-center justify-between gap-2 px-5 py-5">
        <Wordmark />
        {onDismiss && (
          <button
            type="button"
            onClick={onDismiss}
            className="rounded-md p-1.5 text-brand-200 hover:bg-white/10 md:hidden"
          >
            <CloseIcon />
            <span className="sr-only">Close navigation</span>
          </button>
        )}
      </div>

      <nav aria-label="Main" className="flex-1 overflow-y-auto px-3 pb-4">
        {NAVIGATION.map((group) => {
          const visible = group.items.filter((item) => !item.permission || item.permission.some(may))
          if (visible.length === 0) return null

          return (
            <div key={group.heading ?? 'primary'} className="mb-5">
              {group.heading && (
                <p className="px-3 pb-2 text-[11px] font-semibold tracking-[0.14em] text-brand-300 uppercase">
                  {group.heading}
                </p>
              )}
              <ul className="space-y-0.5">
                {visible.map((item) => (
                  <li key={item.to}>
                    <NavLink to={item.to} end={item.to === '/'} className={navClass}>
                      <item.icon className="shrink-0" />
                      <span className="truncate">{item.label}</span>
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>
          )
        })}
      </nav>

      {user && (
        <div className="border-t border-white/10 px-3 py-4">
          <div className="px-3 pb-3">
            <p className="truncate text-sm font-medium text-white">{user.fullName}</p>
            <p className="truncate text-xs text-brand-300">{user.email}</p>
            <div className="mt-2">
              <RoleBadge role={user.role} />
            </div>
          </div>

          <NavLink to="/account" className={navClass}>
            <AccountIcon className="shrink-0" />
            <span>Your account</span>
          </NavLink>

          <button type="button" onClick={handleSignOut} className={cx(NAV_BASE, NAV_IDLE, 'w-full')}>
            <SignOutIcon className="shrink-0" />
            <span>Sign out</span>
          </button>
        </div>
      )}
    </div>
  )
}

const NAV_BASE =
  'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors'
const NAV_IDLE = 'text-brand-100 hover:bg-white/10 hover:text-white'
/** The bronze rule is the only thing that marks the current page, so it has to read at a glance. */
const NAV_ACTIVE = 'bg-white/10 text-white shadow-[inset_2px_0_0_var(--color-accent-500)]'

function navClass({ isActive }: { isActive: boolean }): string {
  return cx(NAV_BASE, isActive ? NAV_ACTIVE : NAV_IDLE)
}
